import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  Button,
  Heading,
  VStack,
  HStack,
  Text,
  Input,
  Textarea,
  useColorModeValue,
  Divider,
  Badge,
  Flex,
  FormControl,
  FormLabel,
  useToast,
  Spinner,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { AddIcon, MinusIcon } from '@chakra-ui/icons';
import api from '../api';

const DebtModal = ({ isOpen, onClose, supplier }) => {
  const toast = useToast();
  
  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const tableBg = useColorModeValue('gray.50', 'gray.700');
  const positiveColor = useColorModeValue('red.500', 'red.400');
  const negativeColor = useColorModeValue('green.500', 'green.400');

  const [debtData, setDebtData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDeductForm, setShowDeductForm] = useState(false);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (isOpen && supplier) {
      fetchDebtData();
    }
  }, [isOpen, supplier]);

  const fetchDebtData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/debts/supplier-${supplier.id}/`);
      setDebtData(response.data);
    } catch (error) {
      console.error('Failed to fetch debt data:', error);
      toast({
        title: 'Помилка',
        description: 'Не вдалося завантажити дані про борг',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddDebt = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({
        title: 'Помилка',
        description: 'Введіть коректну суму',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setActionLoading(true);
    try {
      // If debt doesn't exist, create it first
      let debtId = debtData?.id;
      if (!debtId) {
        const createResponse = await api.post('/debts/', {
          supplier: supplier.id,
        });
        debtId = createResponse.data.id;
      }

      await api.post(`/debts/${debtId}/add-debt/`, {
        amount: parseFloat(amount),
        description: description || `Додано борг вручну`,
      });

      toast({
        title: 'Успішно',
        description: `Борг збільшено на ${amount} грн`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setAmount('');
      setDescription('');
      setShowAddForm(false);
      fetchDebtData();
    } catch (error) {
      console.error('Failed to add debt:', error);
      toast({
        title: 'Помилка',
        description: 'Не вдалося додати борг',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeductDebt = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({
        title: 'Помилка',
        description: 'Введіть коректну суму',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!debtData?.id) {
      toast({
        title: 'Помилка',
        description: 'Немає активного боргу',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setActionLoading(true);
    try {
      await api.post(`/debts/${debtData.id}/deduct-debt/`, {
        amount: parseFloat(amount),
        description: description || `Оплата отримана`,
      });

      toast({
        title: 'Успішно',
        description: `Борг зменшено на ${amount} грн`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setAmount('');
      setDescription('');
      setShowDeductForm(false);
      fetchDebtData();
    } catch (error) {
      console.error('Failed to deduct debt:', error);
      toast({
        title: 'Помилка',
        description: 'Не вдалося зменшити борг',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('uk-UA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent
        bg={cardBg}
        borderRadius="2xl"
        border="1px solid"
        borderColor={borderColor}
        boxShadow={`0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}`}
        position="relative"
        overflow="hidden"
        maxH="90vh"
      >
        {/* Gradient accent */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="6px"
          bg="linear-gradient(90deg, #f093fb, #f5576c, #feca57, #48dbfb)"
        />

        <ModalHeader pb={4} pt={8} bg={cardBg} borderTopRadius="2xl">
          <VStack align="flex-start" spacing={2}>
            <Heading
              size="lg"
              bgGradient="linear(to-r, #f093fb, #f5576c)"
              bgClip="text"
              fontWeight="bold"
            >
              💰 Борг
            </Heading>
            <Text fontSize="md" color={subtleTextColor} fontWeight="normal">
              {supplier?.name || 'N/A'}
            </Text>
          </VStack>
        </ModalHeader>
        
        <ModalCloseButton
          top={6}
          right={6}
          size="lg"
          borderRadius="full"
          _hover={{
            bg: hoverBg,
            color: textColor,
          }}
        />

        <ModalBody bg={cardBg} pb={6}>
          {loading ? (
            <Center py={10}>
              <Spinner size="xl" color="#f093fb" thickness="4px" />
            </Center>
          ) : (
            <VStack spacing={6} align="stretch">
              {/* Current Debt Amount */}
              <Box
                bg="linear-gradient(135deg, #f093fb, #f5576c)"
                borderRadius="xl"
                p={6}
                boxShadow="0 10px 20px rgba(240, 147, 251, 0.3)"
              >
                <VStack spacing={2}>
                  <Text fontSize="sm" color="white" fontWeight="medium">
                    Поточний борг
                  </Text>
                  <Heading size="2xl" color="white">
                    {formatAmount(debtData?.total_amount || 0)} грн
                  </Heading>
                </VStack>
              </Box>

              {/* Action Buttons */}
              <HStack spacing={4}>
                <Button
                  leftIcon={<AddIcon />}
                  flex="1"
                  size="lg"
                  bg="linear-gradient(135deg, #ff6b6b, #ee5a6f)"
                  color="white"
                  borderRadius="xl"
                  boxShadow="0 4px 15px rgba(255, 107, 107, 0.4)"
                  _hover={{
                    bg: "linear-gradient(135deg, #ff5252, #e84c61)",
                    transform: "translateY(-2px)",
                  }}
                  onClick={() => {
                    setShowAddForm(!showAddForm);
                    setShowDeductForm(false);
                    setAmount('');
                    setDescription('');
                  }}
                  isDisabled={showAddForm}
                >
                  Додати борг
                </Button>
                <Button
                  leftIcon={<MinusIcon />}
                  flex="1"
                  size="lg"
                  bg="linear-gradient(135deg, #26de81, #20bf6b)"
                  color="white"
                  borderRadius="xl"
                  boxShadow="0 4px 15px rgba(38, 222, 129, 0.4)"
                  _hover={{
                    bg: "linear-gradient(135deg, #20c972, #1da85f)",
                    transform: "translateY(-2px)",
                  }}
                  onClick={() => {
                    setShowDeductForm(!showDeductForm);
                    setShowAddForm(false);
                    setAmount('');
                    setDescription('');
                  }}
                  isDisabled={showDeductForm || !debtData?.id}
                >
                  Зменшити борг
                </Button>
              </HStack>

              {/* Add Debt Form */}
              {showAddForm && (
                <Box
                  bg={tableBg}
                  borderRadius="xl"
                  p={6}
                  border="2px solid"
                  borderColor="#ff6b6b"
                >
                  <VStack spacing={4}>
                    <FormControl>
                      <FormLabel color={textColor} fontWeight="semibold">
                        💵 Сума (грн)
                      </FormLabel>
                      <Input
                        type="number"
                        step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="Введіть суму"
                        size="lg"
                        borderRadius="lg"
                        _focus={{
                          borderColor: "#ff6b6b",
                          boxShadow: "0 0 0 1px rgba(255, 107, 107, 0.3)",
                        }}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel color={textColor} fontWeight="semibold">
                        📝 Опис (необов'язково)
                      </FormLabel>
                      <Textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Введіть опис..."
                        size="lg"
                        borderRadius="lg"
                        _focus={{
                          borderColor: "#ff6b6b",
                          boxShadow: "0 0 0 1px rgba(255, 107, 107, 0.3)",
                        }}
                      />
                    </FormControl>
                    <HStack spacing={3} w="full">
                      <Button
                        flex="1"
                        bg="linear-gradient(135deg, #ff6b6b, #ee5a6f)"
                        color="white"
                        size="lg"
                        borderRadius="xl"
                        onClick={handleAddDebt}
                        isLoading={actionLoading}
                      >
                        ✅ Підтвердити
                      </Button>
                      <Button
                        flex="1"
                        variant="ghost"
                        size="lg"
                        borderRadius="xl"
                        onClick={() => {
                          setShowAddForm(false);
                          setAmount('');
                          setDescription('');
                        }}
                      >
                        ❌ Скасувати
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              )}

              {/* Deduct Debt Form */}
              {showDeductForm && (
                <Box
                  bg={tableBg}
                  borderRadius="xl"
                  p={6}
                  border="2px solid"
                  borderColor="#26de81"
                >
                  <VStack spacing={4}>
                    <FormControl>
                      <FormLabel color={textColor} fontWeight="semibold">
                        💵 Сума (грн)
                      </FormLabel>
                      <Input
                        type="number"
                        step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="Введіть суму"
                        size="lg"
                        borderRadius="lg"
                        _focus={{
                          borderColor: "#26de81",
                          boxShadow: "0 0 0 1px rgba(38, 222, 129, 0.3)",
                        }}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel color={textColor} fontWeight="semibold">
                        📝 Опис (необов'язково)
                      </FormLabel>
                      <Textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Введіть опис..."
                        size="lg"
                        borderRadius="lg"
                        _focus={{
                          borderColor: "#26de81",
                          boxShadow: "0 0 0 1px rgba(38, 222, 129, 0.3)",
                        }}
                      />
                    </FormControl>
                    <HStack spacing={3} w="full">
                      <Button
                        flex="1"
                        bg="linear-gradient(135deg, #26de81, #20bf6b)"
                        color="white"
                        size="lg"
                        borderRadius="xl"
                        onClick={handleDeductDebt}
                        isLoading={actionLoading}
                      >
                        ✅ Підтвердити
                      </Button>
                      <Button
                        flex="1"
                        variant="ghost"
                        size="lg"
                        borderRadius="xl"
                        onClick={() => {
                          setShowDeductForm(false);
                          setAmount('');
                          setDescription('');
                        }}
                      >
                        ❌ Скасувати
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              )}

              <Divider />

              {/* Transaction History */}
              <Box>
                <Heading size="md" color={textColor} mb={4}>
                  📊 Історія транзакцій
                </Heading>
                {debtData?.transactions && debtData.transactions.length > 0 ? (
                  <TableContainer
                    borderRadius="xl"
                    border="1px solid"
                    borderColor={borderColor}
                    overflow="hidden"
                  >
                    <Table variant="simple" size="sm">
                      <Thead bg={tableBg}>
                        <Tr>
                          <Th>Дата</Th>
                          <Th>Тип</Th>
                          <Th isNumeric>Сума</Th>
                          <Th>Опис</Th>
                          <Th>Користувач</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {debtData.transactions.map((transaction) => (
                          <Tr key={transaction.id}>
                            <Td fontSize="sm">{formatDate(transaction.timestamp)}</Td>
                            <Td>
                              <Badge
                                colorScheme={transaction.transaction_type === 'increase' ? 'red' : 'green'}
                                borderRadius="md"
                                px={2}
                                py={1}
                              >
                                {transaction.transaction_type === 'increase' ? '📈 Збільшення' : '📉 Зменшення'}
                              </Badge>
                            </Td>
                            <Td
                              isNumeric
                              fontWeight="bold"
                              color={transaction.transaction_type === 'increase' ? positiveColor : negativeColor}
                            >
                              {transaction.transaction_type === 'increase' ? '+' : '-'}
                              {formatAmount(transaction.amount)} грн
                            </Td>
                            <Td fontSize="sm" color={subtleTextColor}>
                              {transaction.description || 'Немає опису'}
                            </Td>
                            <Td fontSize="sm" color={subtleTextColor}>
                              {transaction.performed_by_username || 'Система'}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box
                    bg={tableBg}
                    borderRadius="xl"
                    p={8}
                    textAlign="center"
                  >
                    <Text color={subtleTextColor}>
                      📋 Немає транзакцій
                    </Text>
                  </Box>
                )}
              </Box>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter bg={cardBg} borderBottomRadius="2xl" p={6}>
          <Button
            variant="ghost"
            onClick={onClose}
            size="lg"
            borderRadius="xl"
            px={8}
            color={textColor}
            _hover={{
              bg: hoverBg,
            }}
          >
            ❌ Закрити
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DebtModal;

